// The product receipt page (/payment/product/success/<purchase>): the
// API's answer and what the page derives from it. Port of v1's
// payment/product/success/[productPurchaseID] (getProductPurchaseData,
// ProductPurchaseSuccessClient). Pure.

import { ordinal } from "../../dates";

export type ProductLearningContent = {
  contentType?: string;
  contentLink?: string;
  instructionText?: string;
};

export type PurchasedProduct = {
  _id?: string;
  name?: string;
  slug?: string;
  category?: string;
  productType?: string;
  purchaseMessage?: string;
  learningContent?: ProductLearningContent[];
  cover?: string;
  whatsIncluded?: string[];
};

export type ProductPurchase = {
  _id: string;
  createdAt?: string;
  quantity?: number;
  accounting?: { userCharged?: number; giftingFee?: number; postageFee?: number };
  product?: PurchasedProduct | null;
  /** The selling school: its id (the stored field), or populated. */
  school?: string | { _id?: string; name?: string } | null;
};

/** GET /widget/pages/payment/product/success/:id. `widgetCustomization`
 * is also sent; v1 never read it. */
export type ProductSuccessPage = {
  school?: { _id?: string; slug?: string; name?: string } | null;
  productPurchase?: ProductPurchase | null;
};

/** The school's name, as the note is signed (v1 read the purchase's
 * school, which the endpoint leaves an id; the page's school has it). */
export function schoolNameOf(page: ProductSuccessPage): string {
  const own = page.productPurchase?.school;
  if (own && typeof own === "object" && own.name) return own.name;
  return page.school?.name ?? "";
}

const CONTENT_TITLES: Record<string, string> = {
  guide: "Click the link below to access your learning guide",
  video: "Click the access online video content",
};
const CONTENT_LINKS: Record<string, string> = {
  guide: "Learning Guide",
  video: "Video Link",
};

export type LearningLink = { title: string; label: string; href?: string };

/** One card per learning content, v1's wording per type. A type v1 had no
 * words for gets generic ones (v1 showed an empty card), and only an
 * http(s) link is offered. */
export function learningLinks(contents: ProductLearningContent[] | undefined): LearningLink[] {
  return (contents ?? []).map((c) => {
    const type = c.contentType ?? "";
    const href = typeof c.contentLink === "string" && /^https?:\/\//i.test(c.contentLink.trim()) ? c.contentLink.trim() : undefined;
    return {
      title: CONTENT_TITLES[type] ?? "Click the link below to access your content",
      label: CONTENT_LINKS[type] ?? "Open link",
      href,
    };
  });
}

/** The instructions given with the learning content, in order. */
export const instructionsOf = (contents: ProductLearningContent[] | undefined): string[] =>
  (contents ?? []).map((c) => c.instructionText?.trim() ?? "").filter(Boolean);

/** Whether a text is long enough for v1's "Read more" (ExpandableText's
 * 100 words). */
export const isLongText = (text: string, maxWords = 100) => text.trim().split(/\s+/).filter(Boolean).length > maxWords;

/** "3rd March 2026" (v1's moment "Do MMMM YYYY"), in the browser's zone
 * unless one is given; "" when the date isn't one. */
export function purchaseDate(iso: string | undefined, timeZone?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone }).formatToParts(d);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  const day = Number(part("day"));
  return `${ordinal(day)} ${part("month")} ${part("year")}`;
}

/** v1's mount-time `analytics.page("Success Page", …)`: the dataLayer
 * entry it pushed, field for field (`where` is the page's address,
 * referrer and title). */
export function successPageEvent(page: ProductSuccessPage, where: { path: string; referrer: string; title: string; url: string }) {
  const purchase = page.productPurchase;
  return {
    event: "Success Page",
    name: "Success Page",
    path: where.path,
    referrer: where.referrer,
    title: where.title,
    url: where.url,
    source: "widget",
    type: "Kit",
    bookingID: purchase?._id,
    totalPrice: purchase?.accounting?.userCharged,
    giftingFee: purchase?.accounting?.giftingFee,
    postageFee: purchase?.accounting?.postageFee,
    itemName: purchase?.product?.name,
    itemID: purchase?.product?._id,
    schoolName: schoolNameOf(page),
    school: page.school?.slug,
    itemCategory: purchase?.product?.category,
    isNewOrder: true,
    itemTotal: purchase?.quantity,
  };
}
